import { useMemo } from "react";

import BulkManageDesktop from "common/components/BulkManageDesktop";
import { DataTable } from "common/components/DataTable";
import useDataTable from "common/components/DataTable/useDataTable";
import ProfileHeading from "common/components/ProfileHeading";
import Skeleton from "common/components/ui/Skeleton";
import { typedObjectEntries } from "common/helpers/utils";
import type { LinkedInProfile } from "common/types";

import {
  EXCLUDED_LIST_LEADS_ACTIONS,
  LIST_LEADS_COLUMNS,
} from "../../../../constants";
import RemoveExcludedLeads from "../ManageLeads/RemoveExcludedLeads";

import type { ColumnDef } from "@tanstack/react-table";

interface DesktopTableProps {
  listId: string;
  leads: LinkedInProfile[];
  isLoading: boolean;
  pageSize: number;
  totalItemCount: number;
  selectedLeads: LinkedInProfile[];
  setSelectedLeads: (selectedLeads: LinkedInProfile[]) => void;
}

export default function DesktopTable({
  listId,
  leads,
  isLoading,
  pageSize,
  totalItemCount,
  selectedLeads,
  setSelectedLeads,
}: DesktopTableProps) {
  const columns = useMemo<ColumnDef<LinkedInProfile>[]>(
    () => [
      {
        header: "Name",
        cell: ({ row: { original } }) => {
          const { picture_url, full_name, occupation } = original;

          return (
            <ProfileHeading
              fullName={full_name}
              pictureUrl={picture_url}
              isLoading={isLoading}
              occupation={occupation}
            />
          );
        },
        enableHiding: false,
      },
      ...typedObjectEntries(LIST_LEADS_COLUMNS).map(
        ([_, { label, icon, renderTag }]): ColumnDef<LinkedInProfile> => ({
          header: label,
          cell: ({ row: { original } }) => {
            if (isLoading) {
              return <Skeleton className="h-4 w-32" />;
            }
            return renderTag(original);
          },
          meta: { icon, className: "min-w-40 max-w-40" },
        }),
      ),
    ],
    [isLoading],
  );

  const table = useDataTable(
    {
      data: leads || new Array(pageSize).fill({}),
      columns,
    },
    {
      selectedRows: selectedLeads,
      onRowSelectionChange: setSelectedLeads,
    },
  );

  return (
    <DataTable
      table={table}
      totalItemCount={totalItemCount}
      isLoading={isLoading}
      selectionActionsComponent={
        <BulkManageDesktop
          actions={EXCLUDED_LIST_LEADS_ACTIONS}
          selection={selectedLeads}
          clearSelection={() => setSelectedLeads([])}
          renderActionComponent={({ onClose }) => (
            <RemoveExcludedLeads
              listId={listId}
              selectedLeads={selectedLeads}
              onClose={(shouldClearSelection) => {
                onClose();
                if (shouldClearSelection) {
                  setSelectedLeads([]);
                }
              }}
            />
          )}
        />
      }
    />
  );
}
